import type { IconName } from '@/lib/icons';
import type { NavRole } from '@/components/layout/header/navigation-registry';
import type { UserRole } from '@/types/auth.types';

export interface SearchRoute {
  title: string;
  description: string;
  keywords: string[];
  href: string;
  icon: IconName;
  group: string;
  roles: NavRole[];
}

export function filterRoutesForRole(routes: SearchRoute[], role: UserRole | 'guest'): SearchRoute[] {
  const routeRole = role === 'super_admin' ? 'admin' : role;
  return routes.filter((route) => route.roles.includes(routeRole));
}

export function buildRouteRegistry(lang: string, t: Record<string, string>): SearchRoute[] {
  const b = `/${lang}`;
  return [
    { title: t.record, description: t.recordDescription, keywords: ['audio', 'speak', 'record', 'enregistrer', 'parler', 'voice', 'voix'], href: `${b}/speak-contribute`, icon: 'mic', group: t.navAudioSection || 'Audio', roles: ['guest', 'user', 'collector', 'moderator', 'admin', 'accountant'] },
    { title: t.validateAudio, description: t.validateAudioDescription, keywords: ['validate', 'audio', 'valider', 'ecouter'], href: `${b}/validate-audio`, icon: 'volume2', group: t.navAudioSection || 'Audio', roles: ['guest', 'user', 'collector', 'moderator', 'admin', 'accountant'] },
    { title: t.collectorSpaceLabel || 'Espace collecteur', description: t.collectorSpaceDescription || '', keywords: ['collector', 'field', 'collecteur', 'terrain', 'community', 'communaute'], href: `${b}/collector`, icon: 'users', group: t.navCommunitySection || 'Communaute', roles: ['collector'] },
    { title: t.transcribeAudio, description: t.transcribeAudioDescription, keywords: ['transcribe', 'transcription', 'transcrire', 'texte', 'write', 'ecrire'], href: `${b}/transcription`, icon: 'fileText', group: t.navTextSection || 'Texte', roles: ['guest', 'user', 'collector', 'moderator', 'admin', 'accountant'] },
    { title: t.transcribePhrases || 'Transcrire phrases', description: t.transcribePhrasesDescription || '', keywords: ['transcribe', 'phrases', 'transcrire', 'phrases'], href: `${b}/transcription-phrase`, icon: 'languages', group: t.navTextSection || 'Texte', roles: ['guest', 'user', 'collector', 'moderator', 'admin', 'accountant'] },
    { title: t.validateTranscriptions, description: t.validateTranscriptionsDescription, keywords: ['validate', 'transcription', 'valider', 'correction'], href: `${b}/validate-transcription`, icon: 'checkCircle', group: t.navTextSection || 'Texte', roles: ['guest', 'user', 'collector', 'moderator', 'admin', 'accountant'] },
    { title: t.labelImages, description: t.labelImagesDescription, keywords: ['label', 'images', 'image', 'etiqueter', 'photo'], href: `${b}/label-images`, icon: 'image', group: t.navImagesSection || 'Images', roles: ['guest', 'user', 'collector', 'moderator', 'admin', 'accountant'] },
    { title: t.leaderboard || 'Classement', description: t.joinTheBestDesc || '', keywords: ['leaderboard', 'ranking', 'classement', 'top', 'points'], href: `${b}/leaderboard`, icon: 'target', group: t.navProgressSection || 'Progression', roles: ['guest', 'user', 'collector', 'moderator', 'admin', 'accountant'] },
    { title: t.myContributionSummary || t.myContributions || 'Mes contributions', description: t.myContributionSummaryDescription || '', keywords: ['contributions', 'mes', 'my', 'history', 'historique'], href: `${b}/my-contributions`, icon: 'barChart3', group: t.navProgressSection || 'Progression', roles: ['user', 'collector'] },
    { title: t.getPaidLabel || 'Obtenir un paiement', description: t.getPaidDescription || '', keywords: ['paid', 'paiement', 'payment', 'money', 'argent', 'getpaid'], href: `${b}/get-paid`, icon: 'download', group: t.navProgressSection || 'Progression', roles: ['user', 'collector'] },
    { title: t.initialAudio || 'Audio initial', description: t.initialAudioDescription || '', keywords: ['initial', 'audio', 'debut', 'start'], href: `${b}/initial-audio`, icon: 'mic', group: t.navInitialData || 'Donnees initiales', roles: ['guest', 'user', 'collector', 'moderator', 'admin', 'accountant'] },
    { title: t.initialText || 'Texte initial', description: t.initialTextDescription || '', keywords: ['initial', 'text', 'texte', 'debut', 'start'], href: `${b}/initial-text`, icon: 'fileText', group: t.navInitialData || 'Donnees initiales', roles: ['guest', 'user', 'collector', 'moderator', 'admin', 'accountant'] },
    { title: t.initialTranscription || 'Transcription initiale', description: t.initialTranscriptionDescription || '', keywords: ['initial', 'transcription', 'transcrire', 'audio'], href: `${b}/initial-transcription`, icon: 'languages', group: t.navInitialData || 'Donnees initiales', roles: ['user', 'collector', 'moderator', 'admin'] },
    { title: t.initialReview || 'Relecture initiale', description: t.initialReviewDescription || '', keywords: ['initial', 'review', 'relecture', 'validation'], href: `${b}/initial-review`, icon: 'checkCircle', group: t.navInitialData || 'Donnees initiales', roles: ['user', 'collector', 'moderator', 'admin'] },
    { title: t.settings || 'Parametres', description: '', keywords: ['settings', 'parametres', 'compte', 'account', 'profile', 'profil'], href: `${b}/settings`, icon: 'settings', group: 'Compte', roles: ['user', 'collector'] },
    { title: t.dashboard || 'Tableau de bord', description: '', keywords: ['dashboard', 'tableau', 'bord', 'home', 'accueil'], href: `${b}/dashboard`, icon: 'layoutDashboard', group: 'Navigation', roles: ['guest', 'user', 'collector'] },
  ];
}

function segmentScore(segment: string, candidate: string): number {
  if (segment === candidate) return 10;
  if (candidate.startsWith(segment)) return 7;
  if (candidate.includes(segment)) return 5;
  if (segment.length >= 3 && levenshtein(segment, candidate) <= 2) return 3;
  return 0;
}

function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[m][n];
}

export function findSimilarRoutes(pathname: string, routes: SearchRoute[], limit = 5): SearchRoute[] {
  const segments = pathname.split('/').filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? '';

  const scored = routes.map((route) => {
    const routeSegments = route.href.split('/').filter(Boolean);
    let score = 0;

    for (const seg of segments) {
      for (const rSeg of routeSegments) {
        score += segmentScore(seg.toLowerCase(), rSeg.toLowerCase());
      }
    }

    const titleWords = route.title.toLowerCase().split(/\s+/);
    for (const word of titleWords) {
      if (lastSegment && segmentScore(lastSegment.toLowerCase(), word) > 0) {
        score += 4;
      }
    }

    for (const kw of route.keywords) {
      if (lastSegment && segmentScore(lastSegment.toLowerCase(), kw.toLowerCase()) > 0) {
        score += 3;
      }
    }

    return { route, score };
  });

  return scored
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((s) => s.route);
}
